import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { toast } from "@jti/core-components/components/Toaster";
import type {
  DynamicGalleryCollection,
  LastSelectedElement,
  ProductDetailsData,
  SelectedBundleItem,
} from "@jti/fe-common/types";
import { useMessageBus } from "@jti/fe-message-bus";
import { type IBundleOptionInput, useBrandConsumablesMatrixQuery } from "@jti/magento";
import type { ApiMagentoPriceTier, MagentoProductTypeName } from "@jti/magento-types";
import { setUsersDeviceStatus } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { DEPRECATED_Loading } from "@jti/ui/components/DEPRECATED_Loading";
import { RegisterDeviceDialog } from "@jti/ui/components/RegisterDeviceDialog";
import { TradeInEligibilityBanner } from "@jti/ui/components/TradeInEligibilityBanner";
import { TrustPilotStars } from "@jti/ui/components/TrustPilotStars";
import { useTrialStatus } from "@jti/ui/hooks/useTrialStatus";
import { useUser } from "@jti/ui/hooks/useUser";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import {
  AddProductButton,
  AddToCartButtonWrapper,
  ProductBundles,
  ProductConfigurables,
  ProductDescription,
  ProductHeading,
  ProductPromoOffer,
  ProductQuantityError,
  PromoButton,
  StockStatus,
  TradeInDisabledBlock,
  TradeInSignInBanner,
  TryBundleButton,
} from "@product-details/components";
import { ConsentBlock } from "@product-details/components/ConsentBlock";
import { ProductCarousel } from "@product-details/components/ProductCarousel";
import { ProductCta } from "@product-details/components/ProductCta/ProductCta";
import {
  prepareInitialDynamicGallery,
  retriveImageCollection,
} from "@product-details/components/ProductDynamicBundleGallery/helpers/helpers";
import { ProductDynamicBundleGallery } from "@product-details/components/ProductDynamicBundleGallery/ProductDynamicBundleGallery";
import type {
  LoyaltyBadgeCopyConfig,
  LoyaltyPriceTagState,
  LoyaltyRewardCopyConfig,
} from "@product-details/components/ProductLoyaltyBlock";
import { ProductLoyaltyBlock } from "@product-details/components/ProductLoyaltyBlock";
import { ProductPrice } from "@product-details/components/ProductPrice";
import type { ConsentValueType } from "@product-details/containers/StandardProductDetailsContainer/components/types";
import { useTradeInDisabled } from "@product-details/containers/StandardProductDetailsContainer/hooks";
import { useProductData, useSyncProductWithCart, type UseSyncProductWithCartProductData } from "@product-details/hooks";
import { useRedirectOnTradeIn } from "@product-details/hooks/useRedirectOnTradeIn";
import { useScrollToFirstError } from "@product-details/hooks/useScrollToFirstError";
import { getPromoButtonType, PROMO_BUTTON_TYPE } from "@product-details/hooks/useSpecialPromo";
import { PriceTiersContext } from "@product-details/providers/PriceTiersContext";
import { dispatchViewItemEvent } from "@product-details/utils/dispatchViewItemEvent";
import { getFilteredImagesByRole } from "@product-details/utils/getFilteredImagesByRole";
import { getMergedAssets } from "@product-details/utils/getMergedAssets";
import getQuantityParam from "@product-details/utils/getQuantityParam";

import { deviceConfigurationToRegisterDialogPropsAdapter } from "./utils";

import "../../../../ProductDetails.scss";

type StandardProductDetailsProps = ProductDetailsData & {
  loyaltyBadgeCopy?: LoyaltyBadgeCopyConfig;
  loyaltyRewardCopy?: LoyaltyRewardCopyConfig;
  hasLoyaltyDiscountBadge?: boolean;
  isLoyaltyRewardCopyAuthoringEnabled?: boolean;
};

type ValidationMap = {
  bundles: boolean;
  consent: boolean;
};

export const StandardProductDetails = ({
  badgeText = "",
  overwriteBadgeText = "",
  bundlesConfig,
  categories = [],
  configurableOptions,
  currentVariantId = "0",
  currentVariantSku,
  description,
  dynamicGallery,
  galleryAssets,
  hasBadge = false,
  hasDiscountBadge,
  hasLoyaltyDiscountBadge = false,
  hasStrikethroughPrice,
  hideCta,
  isAuthorMode = false,
  isBundle = false,
  isCommerceDisabled,
  isDynamicGalleryEnabled,
  isMultiPackEnabled,
  isTrial = false,
  knowMoreUrl,
  multiPackDescription,
  multiPackName,
  multiPackProducts,
  productCta,
  productName,
  sku,
  trustpilot,
  videoGallery,
  hidePrice,
  isTradeIn,
  nonTradeInSku,
  hasRegistrationConsent,
  consentSectionConfiguration,
  mandatoryPositiveConsent,
  customBuyCtaLabel,
  isTradeInDisclaimerVisible = false,
  tradeInDisclaimerData = {
    title: "",
    description: "",
    ctaText: "",
    ctaLink: "",
  },
  deviceRegistrationConfiguration,
  dynamicGalleryUrls,
  multipacksQuantities: multipacksQuantitiesInitial,
  loyaltyBadgeCopy,
  loyaltyRewardCopy,
  isLoyaltyRewardCopyAuthoringEnabled = false,
}: StandardProductDetailsProps) => {
  const {
    outOfStockBundleOptionsNotification = "",
    availableInStoresBundleOptionsNotification = "",
    isTradeInAvailableWithoutLogin = false,
    isTradeInEligibilityBannerEnabled = false,
    tradeInEligibilityBannerTitle = "",
    tradeInEligibilityBannerDescription = "",
  } = useConfig();

  useRedirectOnTradeIn(isTradeIn && !isAuthorMode && !isTradeInAvailableWithoutLogin);

  const pdpRef = useRef<HTMLDivElement>(null);
  const registerDeviceDialogProps = deviceConfigurationToRegisterDialogPropsAdapter(deviceRegistrationConfiguration);

  const [currentBundles, setCurrentBundles] = useState<IBundleOptionInput[]>([]);
  const [errorsMap, setErrorsMap] = useState<ValidationMap>({ bundles: false, consent: false });
  const [selectedPriceTier, setSelectedPriceTier] = useState<ApiMagentoPriceTier | null>(null);
  const [isOutOfStockSelected, setIsOutOfStockSelected] = useState(false);
  const [isAvailableInStoresSelected, setIsAvailableInStoresSelected] = useState(false);
  const [isInStockSelected, setIsInStockSelected] = useState(true);
  const [imageCollection, setImageCollection] = useState<DynamicGalleryCollection[]>(() => {
    if (!isDynamicGalleryEnabled || !isBundle || !dynamicGallery || !dynamicGalleryUrls) return [];

    try {
      return prepareInitialDynamicGallery({
        bundleConfig: bundlesConfig,
        placeholdersArray: dynamicGallery.items,
        multipacksQuantities: multipacksQuantitiesInitial ?? [],
        elementsDynamicGalleryUrls: dynamicGalleryUrls,
      });
    } catch {
      return [];
    }
  });
  const [consentStatus, setConsentStatus] = useState<ConsentValueType | undefined>();
  const [isRegisterDeviceDialogOpen, setIsRegisterDeviceDialogOpen] = useState(false);
  const [loyaltyPriceTagState, setLoyaltyPriceTagState] = useState<LoyaltyPriceTagState>({ status: "hidden" });
  const { __ } = useTranslations();

  const firstCategory = useMemo(() => (categories.length ? [categories[0].id] : []), [categories]);
  const [promoButtonType, setPromoButtonType] = useState<PROMO_BUTTON_TYPE | undefined>(undefined);
  const [isTradeInProductInCart, setIsTradeInProductInCart] = useState(false);
  const [isCartFetched, setIsCartFetched] = useState(false);
  const { isUserLoggedIn, isCheckingUser } = useUser();
  const messageBus = useMessageBus();

  useEffect(() => {
    messageBus.subscribe({
      channel: "cart.items.fetched",
      callback: ({ payload: { items } }) => {
        const tradeInItems = items.filter((item) => !!item.tradeIn);
        setIsTradeInProductInCart(tradeInItems.length > 0);
      },
    });

    messageBus.subscribe({
      channel: "cart.fetched.state",
      callback: ({ payload: { fetched } }) => {
        setIsCartFetched(fetched);
      },
    });

    return () => {
      messageBus.unsubscribe("cart.items.fetched");
    };
  }, [messageBus]);

  const shouldCheckTradeInEligibility = !!isTradeIn && isUserLoggedIn;
  const tradeInDisabledData = useTradeInDisabled(shouldCheckTradeInEligibility);

  const handleOpenRegisterDeviceDialog = useCallback(() => {
    setIsRegisterDeviceDialogOpen(true);
  }, [setIsRegisterDeviceDialogOpen]);

  const handleCloseRegisterDeviceDialog = useCallback(() => {
    setIsRegisterDeviceDialogOpen(false);
  }, [setIsRegisterDeviceDialogOpen]);

  const handleDeviceRegistrationSuccess = useCallback(() => {
    setUsersDeviceStatus(true);
    setIsRegisterDeviceDialogOpen(false);
  }, [setIsRegisterDeviceDialogOpen, setUsersDeviceStatus]);

  const filteredGalleryAssets = useMemo(
    () => getFilteredImagesByRole(galleryAssets, "dynamic_picture_bundle"),
    [galleryAssets]
  );

  const productGalleryItems = useMemo(
    () => getMergedAssets(filteredGalleryAssets, videoGallery),
    [filteredGalleryAssets, videoGallery]
  );

  const { isLoading: isTrialStatusLoading, isTrialAvailable } = useTrialStatus({
    isTrial,
    categoryIds: firstCategory,
  });

  const {
    isProductDataLoading,
    isProductDataLoaded,
    elementsStockStatus,
    isInStock,
    stockStatus,
    priceInformation,
    discount,
    priceTiers,
    singleProductPrice,
    elementsDynamicGalleryUrls,
    productType,
    specialOffer,
    multipacksQuantities,
    flavor,
    subtitle,
    productDisclaimer,
    color,
  } = useProductData({
    sku,
    pageSize: 1,
    isTrial,
    currentVariantSku,
    isDynamicGalleryEnabled,
  });

  const { data: brandMatrix, isSuccess: isBrandMatrixSuccess } = useBrandConsumablesMatrixQuery();

  const productDetails: UseSyncProductWithCartProductData = {
    sku: (productType as MagentoProductTypeName) === "ConfigurableProduct" ? currentVariantSku : sku,
    priceInformation,
    hidePrice,
    isCommerceDisabled,
  };

  const { isCartLoaded, quantityLimit, isQuantityLimitReached, shouldRenderPrice } =
    useSyncProductWithCart(productDetails);

  const { scrollToFirstError } = useScrollToFirstError(pdpRef);

  const isTryNowLoaded = isCartLoaded && !isTrialStatusLoading;
  const isTryNowVisible = isTrial && isTrialAvailable && !isTrialStatusLoading;

  const isAddProductLoaded = isCartLoaded && isCartFetched && tradeInDisabledData.status !== "loading";

  const onError = () => {
    setErrorsMap((prev) => ({ ...prev, bundles: true }));

    // Observe DOM changes to scroll to the first error when it appears
    scrollToFirstError();
  };

  const onSetElement = (element: LastSelectedElement) => {
    if (isDynamicGalleryEnabled) {
      const galleryUrls =
        Object.keys(elementsDynamicGalleryUrls).length > 0 ? elementsDynamicGalleryUrls : (dynamicGalleryUrls ?? {});
      const quantities = multipacksQuantities.length > 0 ? multipacksQuantities : (multipacksQuantitiesInitial ?? []);

      setImageCollection((prevImageCollection) =>
        retriveImageCollection(element, quantities, galleryUrls, prevImageCollection)
      );
    }
  };

  const onSetBundles = useCallback((bundles: SelectedBundleItem[]) => {
    setIsOutOfStockSelected(bundles.some((item: SelectedBundleItem) => item.stockStatus === "OUT_OF_STOCK"));
    setIsAvailableInStoresSelected(
      bundles.some((item: SelectedBundleItem) => item.stockStatus === "AVAILABLE_IN_STORES")
    );
    setIsInStockSelected(bundles.every((item: SelectedBundleItem) => item.isInStock !== false));

    setCurrentBundles(
      bundles.map((item: SelectedBundleItem) => {
        const { value, quantity, quantities, id } = item;

        return {
          value,
          quantity: quantity ?? 0,
          quantities,
          id: Number(id),
        };
      })
    );
  }, []);

  const fireViewItem = () => {
    dispatchViewItemEvent({
      productName,
      sku: currentVariantSku || sku,
      color,
      flavor,
      categories,
      discount,
      priceInformation,
    });
  };

  const priceTiersContextValueMemo = useMemo(() => {
    return {
      singleProductPrice,
      selectedPriceTier,
      setSelectedPriceTier,
    };
  }, [singleProductPrice, selectedPriceTier]);

  // Track product view event on color change, as Magento doesn't consider it a variant change and doesn't trigger an event
  const lastTrackedColorRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (isProductDataLoading) {
      return;
    }

    const quantity = getQuantityParam();
    const defaultPriceTier = (quantity && priceTiers.find((tier) => tier.quantity === quantity)) || priceTiers[0];

    if (lastTrackedColorRef.current !== color) {
      fireViewItem();
      lastTrackedColorRef.current = color;
    }

    setSelectedPriceTier(defaultPriceTier);
  }, [isProductDataLoading, priceTiers, color]);

  useEffect(() => {
    if ((!isProductDataLoading && !isProductDataLoaded) || isCheckingUser) {
      return;
    }
    setPromoButtonType(getPromoButtonType(specialOffer, isUserLoggedIn));
  }, [isProductDataLoading, isProductDataLoaded, specialOffer, isUserLoggedIn, isCheckingUser]);

  const onConsentBlockChange = (value: ConsentValueType) => {
    setConsentStatus(value);
    setErrorsMap((prev) => ({ ...prev, consent: false }));
  };

  const shouldShowMandatoryConsentSection = mandatoryPositiveConsent && consentSectionConfiguration;
  const shouldShowOptionalConsentSection =
    !mandatoryPositiveConsent && hasRegistrationConsent && consentSectionConfiguration;

  const onAddProductToCartClick = () => {
    const isMandatoryConsentMissing = shouldShowMandatoryConsentSection && consentStatus !== true;
    const isOptionalConsentMissing = shouldShowOptionalConsentSection && consentStatus === undefined;

    if (isMandatoryConsentMissing || isOptionalConsentMissing) {
      setErrorsMap((prev) => ({ ...prev, consent: true }));
      toast.error(__("productdetails.addproduct.error.consents"));
      scrollToFirstError();
      throw new Error("Consent must be given before adding to cart");
    }
  };

  const shouldShowBundlesComponent = isBundle && bundlesConfig;
  const isTradeInEnabled = tradeInDisabledData.status === "not-disabled" && !isTradeInProductInCart;
  const shouldShowConsentForTradeIn = isTradeIn ? isTradeInEnabled : true;

  const isConsentSectionVisibleForNonBundle =
    !shouldShowBundlesComponent && shouldShowOptionalConsentSection && shouldShowConsentForTradeIn;

  const isTradeInDisableBlockVisible =
    tradeInDisabledData.status === "error" || tradeInDisabledData.status === "disabled";
  const isTradeInActionAvailable =
    tradeInDisabledData.status === "disabled" && tradeInDisabledData.cause === "missing-registered-device";
  const tradeInBlockRegisterAction = isTradeInActionAvailable ? handleOpenRegisterDeviceDialog : undefined;

  const handleAlertButtonClick = async () => {
    await dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: tradeInDisclaimerData.ctaText,
      cta_location: "product-details_trade-in_disclaimer",
    });

    window.location.href = tradeInDisclaimerData.ctaLink;
  };

  const showTradeInSignInBanner = isTradeInAvailableWithoutLogin && !!isTradeIn && !isUserLoggedIn && !isCheckingUser;

  const isTradeInBranchResolved = !isCheckingUser && tradeInDisabledData.status !== "loading";

  const shouldShowTradeInEligibilityBanner = Boolean(
    isTradeInEligibilityBannerEnabled &&
    isTradeIn &&
    isTradeInBranchResolved &&
    !isTradeInDisclaimerVisible &&
    !showTradeInSignInBanner &&
    !(isTradeInDisableBlockVisible && isUserLoggedIn)
  );

  return (
    <div
      className="productDetails__container"
      data-testid="product-details"
      ref={pdpRef}
      data-trade-in={isTradeIn ? "true" : "false"}
      data-product-type={productType}
    >
      <div className="carousel__wrapper carousel__bottomSpacing productDetails__carousel">
        {isDynamicGalleryEnabled && isBundle ? (
          <ProductDynamicBundleGallery template={dynamicGallery?.layout} imageCollection={imageCollection} />
        ) : (
          <ProductCarousel
            items={productGalleryItems}
            seekLabel={__("productdetails.carousel.video.seek")}
            volumeLabel={__("productdetails.carousel.video.volume")}
            playLabel={__("productdetails.carousel.video.play")}
            replayLabel={__("productdetails.carousel.video.replay")}
            muteLabel={__("productdetails.carousel.video.mute")}
            soundOnLabel={__("productdetails.carousel.video.unmute")}
            showFullscreenLabel={__("productdetails.carousel.video.fullscreen")}
            previousSlideLabel={__("productdetails.carousel.previous_slide")}
            nextSlideLabel={__("productdetails.carousel.next_slide")}
          />
        )}
      </div>

      <div className="productDetails__productInfo">
        <ProductHeading
          description={subtitle}
          productName={productName}
          badgeText={overwriteBadgeText || badgeText}
          hasBadge={overwriteBadgeText ? true : hasBadge}
        />
        {isProductDataLoading ? <DEPRECATED_Loading dataTestId="product-price-loader" /> : null}

        {!isProductDataLoading && shouldRenderPrice && (
          <ProductPrice
            hideDiscountBadge={!hasDiscountBadge}
            hideLoyaltyDiscountBadge={!hasLoyaltyDiscountBadge}
            hideStrikethroughPrice={!hasStrikethroughPrice}
            priceInformation={priceInformation}
            discount={discount}
            productDisclaimer={productDisclaimer}
            loyaltyPriceTagState={loyaltyPriceTagState}
            loyaltyBadgeCopy={loyaltyBadgeCopy}
          />
        )}

        {trustpilot?.isEnabled && (
          <div className="productDetails__trustpilot-widget">
            <TrustPilotStars sku={sku} styleAlignment="left" />
          </div>
        )}

        {isQuantityLimitReached && (
          <ProductQuantityError className="product-details__quantity-error" isVisible quantityLimit={quantityLimit} />
        )}

        {isProductDataLoading && <DEPRECATED_Loading />}

        {specialOffer && (
          <ProductPromoOffer
            title={specialOffer.description.title}
            description={specialOffer.description.description || ""}
            hint={specialOffer.description.hint || ""}
            alertBox={promoButtonType === PROMO_BUTTON_TYPE.SIMILAR_PRODUCTS}
          />
        )}

        <PriceTiersContext.Provider value={priceTiersContextValueMemo}>
          {!isCommerceDisabled && (
            <AddToCartButtonWrapper
              hideCta={hideCta || isCommerceDisabled || !isInStockSelected || !isInStock}
              isMultiPackEnabled={isMultiPackEnabled}
              multiPackDescription={multiPackDescription}
              multiPackName={multiPackName}
              multiPackProducts={multiPackProducts}
              shouldRenderPrice={shouldRenderPrice}
              name={productName}
              priceInformation={priceInformation}
              priceTiers={priceTiers}
              sku={sku}
            >
              {isTryNowVisible && (
                <TryBundleButton sku={sku} isLoaded={isTryNowLoaded} bundle={currentBundles} onError={onError} />
              )}

              {promoButtonType === PROMO_BUTTON_TYPE.LOGIN && <PromoButton type={PROMO_BUTTON_TYPE.LOGIN} />}
              {promoButtonType === PROMO_BUTTON_TYPE.SIMILAR_PRODUCTS && (
                <PromoButton type={PROMO_BUTTON_TYPE.SIMILAR_PRODUCTS} />
              )}

              {!isTrial && promoButtonType === PROMO_BUTTON_TYPE.ADD_TO_CART && (
                <AddProductButton
                  sku={(productType as MagentoProductTypeName) === "ConfigurableProduct" ? currentVariantSku : sku}
                  isLoaded={isAddProductLoaded}
                  bundle={currentBundles}
                  onError={onError}
                  isDisabled={
                    isQuantityLimitReached ||
                    (!!isTradeIn && !isUserLoggedIn) ||
                    tradeInDisabledData.status !== "not-disabled" ||
                    (isTradeInProductInCart && isTradeIn)
                  }
                  customBuyCtaLabel={customBuyCtaLabel}
                  onClick={onAddProductToCartClick}
                  consentStatus={consentStatus}
                />
              )}
            </AddToCartButtonWrapper>
          )}

          {shouldShowMandatoryConsentSection && (
            <ConsentBlock
              onChange={onConsentBlockChange}
              showError={errorsMap["consent"]}
              {...consentSectionConfiguration}
              isMandatory
            />
          )}

          <ProductCta
            productCta={productCta}
            isCommerceDisabled={isCommerceDisabled}
            isProductDataLoading={isProductDataLoading}
            stockStatus={stockStatus}
          />

          {shouldShowTradeInEligibilityBanner && (
            <div className="product-details__warning">
              <TradeInEligibilityBanner
                title={tradeInEligibilityBannerTitle}
                description={tradeInEligibilityBannerDescription}
              />
            </div>
          )}

          {isTradeInDisclaimerVisible && (
            <AlertBanner
              tone="info"
              title={tradeInDisclaimerData.title}
              description={tradeInDisclaimerData.description}
              actions={[
                {
                  label: tradeInDisclaimerData.ctaText,
                  onClick: handleAlertButtonClick,
                },
              ]}
            />
          )}
          {showTradeInSignInBanner && <TradeInSignInBanner />}
          {isTradeInDisableBlockVisible && isUserLoggedIn && (
            <TradeInDisabledBlock
              tradeInDisabledData={tradeInDisabledData}
              nonTradeInSku={nonTradeInSku}
              className="productDetails__trade-in-alert-wrapper"
              onRegisterDeviceClick={tradeInBlockRegisterAction}
            />
          )}

          {isOutOfStockSelected && outOfStockBundleOptionsNotification.length > 0 && (
            <div className="product-details__warning">
              <AlertBanner tone="warning" description={outOfStockBundleOptionsNotification} />
            </div>
          )}
          {isAvailableInStoresSelected && availableInStoresBundleOptionsNotification.length > 0 && (
            <div className="product-details__warning">
              <AlertBanner tone="warning" description={availableInStoresBundleOptionsNotification} />
            </div>
          )}
        </PriceTiersContext.Provider>
        {shouldShowBundlesComponent && !isProductDataLoading && isBrandMatrixSuccess ? (
          <ProductBundles
            config={bundlesConfig}
            brandMatrix={brandMatrix}
            stockStatusElements={elementsStockStatus}
            isStockLoading={isProductDataLoading}
            showValidation={errorsMap["bundles"]}
            multipacksQuantities={multipacksQuantities}
            onSet={onSetBundles}
            setLastSelectedElement={onSetElement}
            dispatchViewItem={fireViewItem}
          >
            {shouldShowOptionalConsentSection && (
              <ConsentBlock
                onChange={onConsentBlockChange}
                showError={errorsMap["consent"]}
                {...consentSectionConfiguration}
                isMandatory={false}
              />
            )}
          </ProductBundles>
        ) : (
          <ProductConfigurables
            options={configurableOptions}
            currentId={currentVariantId}
            stockStatusElements={elementsStockStatus}
          />
        )}
        {isConsentSectionVisibleForNonBundle && (
          <ConsentBlock
            onChange={onConsentBlockChange}
            showError={errorsMap["consent"]}
            {...consentSectionConfiguration}
            isMandatory={false}
          />
        )}
        <div className={"product-information-block"}>
          <StockStatus stockStatus={stockStatus} isProductDataLoading={isProductDataLoading} />
          <ProductDescription description={description} />
          <ProductLoyaltyBlock
            knowMoreUrl={knowMoreUrl}
            sku={sku}
            selectedQuantity={selectedPriceTier?.quantity}
            unitPrice={priceInformation?.finalPrice?.value ?? null}
            currency={priceInformation?.finalPrice?.currency}
            loyaltyRewardCopy={loyaltyRewardCopy}
            isLoyaltyRewardCopyAuthoringEnabled={isLoyaltyRewardCopyAuthoringEnabled}
            onPriceTagStateChange={setLoyaltyPriceTagState}
          />
        </div>
      </div>

      {isRegisterDeviceDialogOpen && (
        <RegisterDeviceDialog
          accordionContentVariant="withImages"
          isOpened={isRegisterDeviceDialogOpen}
          onClose={handleCloseRegisterDeviceDialog}
          onDeviceRegistrationSuccess={handleDeviceRegistrationSuccess}
          {...registerDeviceDialogProps}
        />
      )}
    </div>
  );
};
